import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from "react-router-dom";
import axios from "../../utils/axios";
import { getToken, getUser } from '../../utils/Common';
import Moment from 'moment';
import Swal from 'sweetalert2';
import Layout from '../../components/Layout';
import DataTable from 'react-data-table-component';

function Detail() {
  const FileDownload = require('js-file-download');
  const token = getToken();
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  };
  const history = useHistory();
  const params = useParams();
  const PPID = params.IDPP;

  // Session
  const userData = getUser() || {};
  const uid = userData.id;

  // States
  const [headerPP, setHeaderPP] = useState({});
  const [detailPP, setDetailPP] = useState([]);
  const [flowPP, setFlowPP] = useState({});
  const [listSuppPP, setListSuppPP] = useState([]);
  const [receivedItemIds, setReceivedItemIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState({
    header: false,
    detail: false,
    flow: false,
    supplier: false
  });
  
  // Modal state for image viewing
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageSrc: '',
    imageTitle: '',
    imageRemark: ''
  });
  
  // Time tracking states
  const [hmDay, setHMDay] = useState('');
  const [dooDay, setDOODay] = useState('');
  const [prcDay, setPRCDay] = useState('');
  const [pmDay, setPMDay] = useState('');
  const [caDay, setCADay] = useState('');
  const [dooPRDay, setDOOPRDay] = useState('');
  const [mdPRDay, setMDPRDay] = useState('');

  // Format currency
  const numberFormat = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumSignificantDigits: 5,
    }).format(value);

  // Image cell component with error handling
  const ImageCell = ({ row }) => {
    const [imageError, setImageError] = useState(false);
    
    return (
      <div className="py-2 flex justify-center">
        {row.img_item && !imageError ? (
          <img 
            src={row.img_item} 
            alt={row.nama_item}
            className="w-12 h-12 object-cover rounded cursor-pointer hover:scale-105 transition-transform shadow-sm"
            onError={() => setImageError(true)}
            onClick={() => setImageModal({
              isOpen: true,
              imageSrc: row.img_item,
              imageTitle: row.nama_item,
              imageRemark: row.comment_item || row.remark || ''
            })}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center shadow-sm">
            {imageError ? (
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
        )}
      </div>
    );
  };

  // Supplier Image component with error handling
  const SupplierImage = ({ src, alt, onClick }) => {
    const [imageError, setImageError] = useState(false);

    if (imageError || !src) {
      return (
        <div 
          className="w-10 h-10 rounded bg-gray-100 border border-gray-200 flex items-center justify-center cursor-pointer shadow"
          onClick={onClick}
          title="Image failed to load"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        className="w-10 h-10 rounded object-cover shadow cursor-pointer border border-gray-200"
        onError={() => setImageError(true)}
        onClick={onClick}
      />
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 1: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 2: return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 3: return 'bg-green-100 text-green-800 border-green-300';
      case 22: return 'bg-red-100 text-red-800 border-red-300';
      case 33: return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 1: return 'Waiting HM Approval';
      case 2: return 'Waiting DOO Approval';
      case 3: return 'DOO Approved';
      case 22: return 'HM Rejected';
      case 33: return 'DOO Rejected';
      case 4: return 'PR Created';
      case 5: return 'PM Approved';
      case 55: return 'PM Rejected';
      case 6: return 'CA Approved';
      case 66: return 'CA Rejected';
      case 7: return 'DOO PR Approved';
      case 77: return 'DOO PR Rejected';
      case 8: return 'Bill/MD Approved';
      case 88: return 'Bill/MD Rejected';
      case 9: return 'Waiting DOF Sign';
      case 10: return 'PO Sent';
      case 11: return 'Item Received';
      default: return 'Unknown Status';
    }
  };

  const statusToText = (status) => {
    if (status === null || status === undefined || status === '') return '-';
    const s = typeof status === 'string' ? status.trim().toLowerCase() : status;
    const n = parseInt(s, 10);
    if (!Number.isNaN(n)) {
      switch (n) {
        case 1: return 'Full';
        case 2: return 'Not Full';
        case 3: return 'Defect';
        case 4: return 'Reject';
        default: break;
      }
    } else {
      if (s.includes('full')) return 'Full';
      if (s.includes('not') || s.includes('partial')) return 'Not Full';
      if (s.includes('defect')) return 'Defect';
      if (s.includes('reject')) return 'Reject';
    }
    return String(status);
  };

  const toTitle = (s) => s.replace(/\b\w/g, c => c.toUpperCase());
  const formatMaybeDate = (key, value) => {
    if (!value) return '-';
    if (key.toLowerCase().includes('date')) {
      try {
        return Moment(value).format('DD MMM YYYY, HH:mm');
      } catch {
        return value;
      }
    }
    return value;
  };

  const getFirstValue = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return undefined;
  };

  const getQtyReceive = (s) =>
    getFirstValue(s, ['qty_receive', 'receive_qty', 'qty_received', 'quantity_received', 'qty_receive_item']);
  const getReceiveDate = (s) =>
    getFirstValue(s, ['receive_item_date', 'receive_date', 'date_receive']);
  const getReceiveBy = (s) =>
    getFirstValue(s, ['receive_by', 'received_by']);
  const getReceiveRemark = (s) =>
    getFirstValue(s, ['receive_remark', 'remark_receive', 'receive_note']);

  const buildReceiveInfo = (supplier) => {
    const lines = [];
    lines.push(`Supplier : ${supplier.companyname || '-'}`);
    lines.push(`Item     : ${supplier.nama_item || '-'}`);
    lines.push(`Qty PO   : ${supplier.quantity || 0}`);
    const qtyRec = getQtyReceive(supplier);
    lines.push(`Qty Rec. : ${qtyRec !== undefined ? qtyRec : '-'}`);
    lines.push(`Status   : ${statusToText(supplier.receive_status)}`);
    const rDate = getReceiveDate(supplier);
    lines.push(`Receive Date : ${rDate ? Moment(rDate).format('DD MMM YYYY, HH:mm') : '-'}`);
    lines.push(`Receive By   : ${getReceiveBy(supplier) || '-'}`);
    lines.push(`Receive Remark: ${getReceiveRemark(supplier) || '-'}`);
    lines.push(`PO Remark     : ${supplier.remark_po || '-'}`);
    lines.push(`Valid PO      : ${supplier.valid_po ? Moment(supplier.valid_po).format('DD MMM YYYY') : '-'}`);
    lines.push(`Item Must Receive: ${supplier.item_must_receive ? Moment(supplier.item_must_receive).format('DD MMM YYYY') : '-'}`);
    if (supplier.date_po_send) {
      lines.push(`PO Sent Date : ${Moment(supplier.date_po_send).format('DD MMM YYYY')}`);
    }
    Object.keys(supplier || {}).forEach((k) => {
      if (!k.startsWith('receive_')) return;
      if (['receive_image','receive_status','receive_item_date','receive_remark','receive_by'].includes(k)) return;
      const val = supplier[k];
      if (val === null || val === undefined || String(val).trim() === '') return;
      const raw = k.replace(/^receive_/, '').replace(/_/g, ' ');
      const label = toTitle(raw);
      lines.push(`${label} : ${formatMaybeDate(k, val)}`);
    });
    return lines;
  };

  // Fetch header data
  const getHeaderPP = useCallback(() => {
    axios
      .get(`/header/proposal/${PPID}`, { headers })
      .then(function (response) {
        // Handle if response.data is a string (JSON encoded)
        let data = response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            // Failed to parse header JSON
          }
        }
        
        setHeaderPP(data || {});
        setDataLoaded(prev => ({ ...prev, header: true }));
      })
      .catch(function (error) {
        setDataLoaded(prev => ({ ...prev, header: true }));
      });
  }, [PPID, headers]);

  // Fetch detail items
  const getDetailPP = useCallback(() => {
    axios
      .get(`/detail/proposal/${PPID}`, { headers })
      .then(function (response) {
        // Handle if response.data is a string (JSON encoded)
        let data = response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = [];
          }
        }
        
        setDetailPP(Array.isArray(data) ? data : []);
        setDataLoaded(prev => ({ ...prev, detail: true }));
      })
      .catch(function (error) {
        setDetailPP([]);
        setDataLoaded(prev => ({ ...prev, detail: true }));
      });
  }, [PPID, headers]);

  // Fetch flow data
  const getFlowPP = useCallback(() => {
    axios
      .get(`/flow/${PPID}`, { headers })
      .then(function (response) {
        // Handle if response.data is a string (JSON encoded)
        let data = response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = [];
          }
        }
        
        // Flow data is an array with flow object at index 0
        const flowData = Array.isArray(data) ? data[0] : data;
        setFlowPP(flowData || {});
        
        // Process time data
        if(data && Array.isArray(data)) {
          if(data[0]?.hm_action && data[1]){
            setHMDay(data[1].d + ' day ' + data[1].h + ' hours ' + data[1].i + ' minutes');
          } else {
            setHMDay('Waiting for Action');
          }

          if(data[0]?.doo_action && data[2]){
            setDOODay(data[2].d + ' day ' + data[2].h + ' hours ' + data[2].i + ' minutes');
          } else {
            setDOODay('Waiting for Action');
          }

          if(data[0]?.prcaction && data[3]){
            setPRCDay(data[3].d + ' day ' + data[3].h + ' hours ' + data[3].i + ' minutes');
          } else {
            setPRCDay('Waiting for Action');
          }

          if(data[0]?.pm_action && data[4]){
            setPMDay(data[4].d + ' day ' + data[4].h + ' hours ' + data[4].i + ' minutes');
          } else {
            setPMDay('Waiting for Action');
          }

          if(data[0]?.ca_action && data[5]){
            setCADay(data[5].d + ' day ' + data[5].h + ' hours ' + data[5].i + ' minutes');
          } else {
            setCADay('Waiting for Action');
          }

          if(data[0]?.prdoo && data[6]){
            setDOOPRDay(data[6].d + ' day ' + data[6].h + ' hours ' + data[6].i + ' minutes');
          } else {
            setDOOPRDay('Waiting for Action');
          }

          if(data[0]?.ceo_action && data[7]){
            setMDPRDay(data[7].d + ' day ' + data[7].h + ' hours ' + data[7].i + ' minutes');
          } else {
            setMDPRDay('Waiting for Action');
          }
        }
      })
      .catch(function (error) {
        setDataLoaded(prev => ({ ...prev, flow: true }));
      })
      .finally(() => {
        setDataLoaded(prev => ({ ...prev, flow: true }));
      });
  }, [PPID, headers]);

  // Fetch supplier list
  const getListSupplierPP = useCallback(() => {
    axios
      .get(`/supplier/list/purchase/proposal/${PPID}`, { headers })
      .then(function (response) {
        // Handle if response.data is a string (JSON encoded)
        let data = response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = [];
          }
        }
        
        const normalizedData = Array.isArray(data) ? data : [];
        setListSuppPP(normalizedData);

        const receivedIds = [];
        normalizedData.forEach(item => {
          if (item.receive_image && receivedIds.indexOf(item.id_det_purchase_proposal) === -1) {
            receivedIds.push(item.id_det_purchase_proposal);
          }
        });
        setReceivedItemIds(receivedIds);
        setDataLoaded(prev => ({ ...prev, supplier: true }));
      })
      .catch(function (error) {
        setListSuppPP([]);
        setReceivedItemIds([]);
        setDataLoaded(prev => ({ ...prev, supplier: true }));
      });
  }, [PPID, headers]);

  useEffect(() => {
    if (PPID) {
      // Reset loading states
      setLoading(true);
      setDataLoaded({
        header: false,
        detail: false,
        flow: false,
        supplier: false
      });
      
      // Fetch all data
      getHeaderPP();
      getDetailPP();
      getFlowPP();
      getListSupplierPP();
    }
  }, [PPID]);

  // Check if all data is loaded
  useEffect(() => {
    const allLoaded = Object.values(dataLoaded).every(loaded => loaded === true);
    if (allLoaded) {
      setLoading(false);
    }
  }, [dataLoaded]);

  // Print PR
  const printPR = () => {
    axios
      .get(`/print/pr/${PPID}`, { responseType: 'blob', headers })
      .then(function (response) {
        FileDownload(response.data, `${PPID}_PR.pdf`);
      })
      .catch(function (error) {
        // Error printing
      });
  };

 

  const selectedSuppliers = Array.isArray(listSuppPP)
    ? listSuppPP.filter(s => s.supplier_choose === s.supplier_id)
    : [];

  // DataTable columns for items
  const itemColumns = [
    {
      name: '#',
      width: '50px',
      cell: (row, index) => (
        <div className="text-center text-xs font-medium">{index + 1}</div>
      ),
    },
    {
      name: 'Image',
      width: '70px',
      cell: row => <ImageCell row={row} />
    },
    {
      name: 'Item Name',
      selector: row => row.nama_item,
      wrap: true,
      sortable: true,
      grow: 2,
      cell: row => (
        <div className="text-sm">{row.nama_item}</div>
      ),
    },
    {
      name: 'Qty',
      selector: row => row.quantity,
      width: '80px',
      cell: row => (
        <div className="font-semibold text-gray-800 text-center text-xs">{row.quantity || 0} {row.unit}</div>
      ),
    },
    {
      name: 'Receive',
      width: '90px',
      cell: row => {
        const isReceived = receivedItemIds.includes(row.id_det_purchase_proposal);
        return (
          <div className="flex justify-center">
            <span
              className={
                `px-2 py-0.5 rounded-full text-[10px] font-medium ` +
                (isReceived ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500')
              }
            >
              {isReceived ? 'Received' : 'Waiting'}
            </span>
          </div>
        );
      },
    },
    {
      name: 'Link',
      selector: row => row.url_item,
      width: '60px',
      cell: row => (
        row.url_item ? (
          <a 
            href={row.url_item} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex justify-center"
            title="View online store"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        ) : (
          <span className="text-gray-400 text-xs text-center block">-</span>
        )
      ),
    },
    {
      name: 'Remark',
      selector: row => row.comment_item || row.remark || '-',
      wrap: true,
      grow: 1,
      cell: row => (
        <div className="text-xs text-gray-600">{row.comment_item || row.remark || '-'}</div>
      ),
    },
  ];

  // Timeline data - with null safety
  const timelineSteps = [
    {
      title: 'Created',
      user: headerPP?.name || 'User',
      date: headerPP?.created_at,
      status: 'completed',
      icon: '📝',
    },
    {
      title: 'HM Approval',
      user: 'Hotel Manager',
      date: flowPP?.hm_action,
      time: hmDay,
      comment: flowPP?.hm_comment,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 2 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 22 ? 'rejected' : 'pending',
      icon: '👤',
    },
    {
      title: 'DOO Approval (PP)',
      user: 'Director of Operation',
      date: flowPP?.doo_action,
      time: dooDay,
      comment: flowPP?.doo_comment,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 3 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 33 ? 'rejected' : 'pending',
      icon: '👔',
    },
    {
      title: 'Purchasing',
      user: 'Purchasing Team',
      date: flowPP?.prcaction,
      time: prcDay,
      comment: flowPP?.prcremark,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 4 ? 'completed' : 'pending',
      icon: '🛒',
    },
    {
      title: 'PM Approval',
      user: 'Purchasing Manager',
      date: flowPP?.pm_action,
      time: pmDay,
      comment: flowPP?.pmremark,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 5 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 55 ? 'rejected' : 'pending',
      icon: '📊',
    },
    {
      title: 'CA Approval',
      user: 'Chief Accounting',
      date: flowPP?.ca_action,
      time: caDay,
      comment: flowPP?.ca_remark,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 6 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 66 ? 'rejected' : 'pending',
      icon: '💰',
    },
    {
      title: 'DOO Approval (PR)',
      user: 'Director of Operation',
      date: flowPP?.prdoo,
      time: dooPRDay,
      comment: flowPP?.prdoocomm,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 7 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 77 ? 'rejected' : 'pending',
      icon: '✅',
    },
    {
      title: 'MD/CEO Approval',
      user: 'Managing Director',
      date: flowPP?.ceo_action,
      time: mdPRDay,
      comment: flowPP?.ceo_remark,
      status: (flowPP?.stts_pp || headerPP?.stts_pp) >= 8 ? 'completed' : (flowPP?.stts_pp || headerPP?.stts_pp) === 88 ? 'rejected' : 'pending',
      icon: '👑',
    },
  ];

  // Show loading state
  if (loading) {
    return (
      <Layout title="Document Detail" subtitle="Loading...">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading proposal details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`No: ${headerPP.nomer || ''}`} >
      <div className="p-6">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center space-x-4 mb-2">
               
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(headerPP.stts_pp)}`}>
                  {getStatusText(headerPP.stts_pp)}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                Created: {Moment(headerPP.created_at).format('DD MMM YYYY, HH:mm')}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
             
              <button
                onClick={printPR}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print</span>
              </button>
              
              <button
                onClick={() => {
                  setLoading(true);
                  setDataLoaded({
                    header: false,
                    detail: false,
                    flow: false,
                    supplier: false
                  });
                  // Refresh all data
                  getHeaderPP();
                  getDetailPP();
                  getFlowPP();
                  getListSupplierPP();
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Site</p>
              <p className="font-semibold text-sm text-gray-900">{headerPP.nm_hotel}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Department</p>
              <p className="font-semibold text-sm text-gray-900">{headerPP.nm_department}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Requested By</p>
              <p className="font-semibold text-sm text-gray-900">{headerPP.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Need Date</p>
              <p className="font-semibold text-sm text-gray-900">
                {Moment(headerPP.date_need).format('DD MMM YYYY')}
              </p>
            </div>
          </div>

          {headerPP.remark && (
            <div className="mt-3 p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-500 mb-1">Remark</p>
              <p className="text-sm text-gray-900">{headerPP.remark}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Items Section - 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Items List</h2>
              </div>
              <div className="p-4">
                <DataTable
                  columns={itemColumns}
                  data={Array.isArray(detailPP) ? detailPP : []}
                  customStyles={{
                    rows: {
                      style: {
                        minHeight: '60px',
                        fontSize: '13px',
                        '&:hover': {
                          backgroundColor: '#f3f4f6',
                        },
                      },
                    },
                    headCells: {
                      style: {
                        backgroundColor: '#f9fafb',
                        fontWeight: '700',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.025em',
                        color: '#4b5563',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                      },
                    },
                    cells: {
                      style: {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        paddingLeft: '10px',
                        paddingRight: '10px',
                      },
                    },
                  }}
                  noDataComponent={
                    <div className="py-8 text-center text-gray-500">
                      No items found
                    </div>
                  }
                  progressPending={loading}
                  striped
                  highlightOnHover
                />
              </div>
            </div>

            {/* Supplier Comparison */}
            {listSuppPP && listSuppPP.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mt-4">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Supplier Comparison</h2>
                    {selectedSuppliers.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Supplier selected: {selectedSuppliers[0]?.companyname}
                      </p>
                    )}
                  </div>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    {selectedSuppliers.length} Selected Supplier{selectedSuppliers.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="p-4">
                  <div className="w-full">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 text-xs">
                          <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">
                            #
                          </th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">
                            Supplier / Item
                          </th>
                          <th className="px-2 py-2 text-center font-medium text-gray-600 uppercase">
                            Qty
                          </th>
                          <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">
                            Price
                          </th>
                          <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">
                            Total
                          </th>
                          <th className="px-2 py-2 text-center font-medium text-gray-600 uppercase">
                            Status
                          </th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">
                            Receive Info
                          </th>
                          <th className="px-1 py-2 text-center font-medium text-gray-600 uppercase">
                            Detail
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 text-xs">
                        {selectedSuppliers.map((supplier, index) => {
                          const subtotal = (supplier.quantity || 0) * (supplier.quotation_price || 0);
                          const isSelected = supplier.supplier_choose === supplier.supplier_id;
                          return (
                            <tr key={index} className={`transition-colors ${isSelected ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'}`}>
                              <td className="px-2 py-2 text-gray-500">
                                {index + 1}
                              </td>
                              <td className="px-2 py-2">
                                <div>
                                  <div className="font-semibold text-gray-900">{supplier.companyname}</div>
                                  <div className="text-gray-600 text-xs">{supplier.nama_item}</div>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-center font-medium text-gray-900">
                                {supplier.quantity || 0}
                              </td>
                              <td className="px-2 py-2 text-right text-gray-900">
                                {numberFormat(supplier.quotation_price || 0)}
                              </td>
                              <td className="px-2 py-2 text-right font-bold text-indigo-600">
                                {numberFormat(subtotal)}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {supplier.receive_image ? (
                                  <div className="flex flex-col items-center space-y-1">
                                    <span className="inline-block px-1.5 py-0.5 text-xs rounded-full bg-green-200 text-green-900 font-medium">
                                      Received
                                    </span>
                                    <SupplierImage
                                      src={supplier.receive_image}
                                      alt={supplier.nama_item}
                                      onClick={() =>
                                        setImageModal({
                                          isOpen: true,
                                          imageSrc: supplier.receive_image,
                                          imageTitle: `Receiving - ${supplier.companyname}`,
                                          imageRemark: buildReceiveInfo(supplier).join('\n'),
                                        })
                                      }
                                    />
                                  </div>
                                ) : supplier.file_po && supplier.date_po_send ? (
                                  <span className="inline-block px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-medium">
                                    Sent
                                  </span>
                                ) : supplier.file_po ? (
                                  <span className="inline-block px-1.5 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">
                                    Ready
                                  </span>
                                ) : (
                                  <span className="inline-block px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 font-medium">
                                    Wait
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-left align-top">
                                {supplier.receive_image ? (
                                  <div className="text-[10px] text-gray-700 space-y-0.5">
                                    <div><span className="font-semibold">Qty Rec.</span>: {getQtyReceive(supplier) ?? '-'}</div>
                                    <div>
                                      <span className="font-semibold">Status</span>: {
                                        statusToText(supplier.receive_status)
                                      }
                                    </div>
                                    <div>
                                      <span className="font-semibold">Date</span>: {
                                        (() => {
                                          const d = getReceiveDate(supplier);
                                          return d ? Moment(d).format('DD MMM YYYY, HH:mm') : '-';
                                        })()
                                      }
                                    </div>
                                    <div><span className="font-semibold">By</span>: {getReceiveBy(supplier) || '-'}</div>
                                    <div><span className="font-semibold">Remark</span>: {getReceiveRemark(supplier) || '-'}</div>
                                    <div><span className="font-semibold">PO Remark</span>: {supplier.remark_po || '-'}</div>
                                    <div>
                                      <span className="font-semibold">Valid PO</span>: {
                                        supplier.valid_po ? Moment(supplier.valid_po).format('DD MMM YYYY') : '-'
                                      }
                                    </div>
                                    <div>
                                      <span className="font-semibold">Must Receive</span>: {
                                        supplier.item_must_receive ? Moment(supplier.item_must_receive).format('DD MMM YYYY') : '-'
                                      }
                                    </div>
                                    {supplier.date_po_send && (
                                      <div>
                                        <span className="font-semibold">PO Sent Date</span>: {Moment(supplier.date_po_send).format('DD MMM YYYY')}
                                      </div>
                                    )}
                                    {Object.keys(supplier || {}).filter(k => k.startsWith('receive_') && !['receive_image','receive_status','receive_item_date','receive_remark','receive_by'].includes(k)).map((k) => (
                                      <div key={k}>
                                        <span className="font-semibold">{toTitle(k.replace(/^receive_/, '').replace(/_/g,' '))}</span>: {formatMaybeDate(k, supplier[k])}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-gray-400">No receiving data</span>
                                )}
                              </td>
                              <td className="px-1 py-2 text-center">
                                {supplier.receive_image ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setImageModal({
                                        isOpen: true,
                                        imageSrc: supplier.receive_image,
                                        imageTitle: `Receiving Detail - ${supplier.companyname}`,
                                        imageRemark: buildReceiveInfo(supplier).join('\n'),
                                      })
                                    }
                                    className="px-2 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700 font-medium hover:bg-blue-200"
                                  >
                                    Detail
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-gray-400">No data</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 font-semibold text-xs">
                          <td colSpan="4" className="px-2 py-2 text-right text-gray-700">
                            Total:
                          </td>
                          <td className="px-2 py-2 text-right text-indigo-700 font-bold">
                            {numberFormat(
                              selectedSuppliers.reduce((total, supplier) => {
                                return total + ((supplier.quantity || 0) * (supplier.quotation_price || 0));
                              }, 0)
                            )}
                          </td>
                          <td colSpan="2" className="px-2 py-2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Timeline Section - 1 column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Approval Timeline</h2>
              </div>
              <div className="p-4">
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
                  {timelineSteps.map((step, index) => (
                    <div key={index} className="relative flex items-start mb-4">
                      {/* Icon */}
                      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full shadow-sm ${
                        step.status === 'completed' ? 'bg-green-100' :
                        step.status === 'rejected' ? 'bg-red-100' :
                        step.status === 'pending' ? 'bg-gray-100' :
                        'bg-gray-100'
                      }`}>
                        <span className="text-lg">{step.icon}</span>
                      </div>
                      
                      {/* Content */}
                      <div className="ml-3 flex-1">
                        <h3 className="text-xs font-semibold text-gray-900">
                          {step.title}
                        </h3>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {step.user}
                        </p>
                        {step.date && (
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            {Moment(step.date).format('DD MMM YYYY, HH:mm')}
                          </p>
                        )}
                        {step.time && step.time !== 'Waiting for Action' && (
                          <p className="text-[10px] text-indigo-600 mt-0.5">
                            Duration: {step.time}
                          </p>
                        )}
                        {step.comment && (
                          <div className="mt-1 p-1.5 bg-gray-50 rounded text-[10px] text-gray-700">
                            {step.comment}
                          </div>
                        )}
                        
                        {/* Status Badge */}
                        <div className="mt-1">
                          {step.status === 'completed' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                              <svg className="w-2.5 h-2.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Approved
                            </span>
                          )}
                          {step.status === 'rejected' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800">
                              <svg className="w-2.5 h-2.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Rejected
                            </span>
                          )}
                          {step.status === 'pending' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                              <svg className="w-2.5 h-2.5 mr-0.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {imageModal.isOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={() => setImageModal({ isOpen: false, imageSrc: '', imageTitle: '', imageRemark: '' })}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b">
                <h3 className="text-sm font-semibold text-gray-900">
                  {imageModal.imageTitle}
                </h3>
                <button
                  onClick={() => setImageModal({ isOpen: false, imageSrc: '', imageTitle: '', imageRemark: '' })}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Image */}
              <div className="p-4">
                <img 
                  src={imageModal.imageSrc} 
                  alt={imageModal.imageTitle}
                  className="max-w-full max-h-[60vh] object-contain mx-auto"
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik0yMDAgMTUwQzE3Ny45MDkgMTUwIDE2MCAxNjcuOTA5IDE2MCAxOTBDMTYwIDIxMi4wOTEgMTc3LjkwOSAyMzAgMjAwIDIzMEMyMjIuMDkxIDIzMCAyNDAgMjEyLjA5MSAyNDAgMTkwQzI0MCAxNjcuOTA5IDIyMi4wOTEgMTUwIDIwMCAxNTBaTTIwMCAyMTBDMTg4Ljk1NCAyMTAgMTgwIDIwMS4wNDYgMTgwIDE5MEMxODAgMTc4Ljk1NCAxODguOTU0IDE3MCAyMDAgMTcwQzIxMS4wNDYgMTcwIDIyMCAxNzguOTU0IDIyMCAxOTBDMjIwIDIwMS4wNDYgMjExLjA0NiAyMTAgMjAwIDIxMFoiIGZpbGw9IiM5Q0E0QUYiLz4KPHBhdGggZD0iTTI4MCAyNDBIMTIwQzEwOS45NTQgMjQwIDEwMiAyNDcuOTU0IDEwMiAyNThaVjI2MEMxMDIgMjcwLjA0NiAxMDkuOTU0IDI4MCAxMjAgMjgwSDI4MEMyOTAuMDQ2IDI4MCAyOTggMjcwLjA0NiAyOTggMjYwVjI1OEMyOTggMjQ3Ljk1NCAyOTAuMDQ2IDI0MCAyODAgMjQwWiIgZmlsbD0iIzlDQTRBRiIvPgo8L3N2Zz4=';
                  }}
                />
                
                {imageModal.imageRemark && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Receiving Information</h4>
                    <div className="space-y-2">
                      {imageModal.imageRemark
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line.length > 0)
                        .map((line, index) => {
                          const [rawLabel, ...rest] = line.split(':');
                          const label = rawLabel ? rawLabel.trim() : '';
                          const value = rest.join(':').trim();

                          if (!label || !value) {
                            return (
                              <p
                                key={index}
                                className="text-sm text-gray-600"
                              >
                                {line}
                              </p>
                            );
                          }

                          if (label.toLowerCase() === 'status') {
                            const statusValue = value.toLowerCase();
                            let badgeClass = 'bg-gray-100 text-gray-800';
                            if (statusValue.includes('full')) {
                              badgeClass = 'bg-green-100 text-green-800';
                            } else if (statusValue.includes('not full')) {
                              badgeClass = 'bg-yellow-100 text-yellow-800';
                            } else if (statusValue.includes('defect')) {
                              badgeClass = 'bg-red-100 text-red-800';
                            } else if (statusValue.includes('reject')) {
                              badgeClass = 'bg-red-100 text-red-800';
                            }

                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-500 font-medium">{label}</span>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClass}`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-current mr-1 animate-pulse"></span>
                                  {value}
                                </span>
                              </div>
                            );
                          }

                          if (label.toLowerCase() === 'supplier' || label.toLowerCase() === 'item') {
                            return (
                              <div
                                key={index}
                                className="text-sm"
                              >
                                <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
                                <p className="font-semibold text-gray-900">{value}</p>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={index}
                              className="flex items-start justify-between text-sm"
                            >
                              <span className="text-gray-500">{label}</span>
                              <span className="ml-4 text-gray-700 text-right">{value}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Detail;
